import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';
import OtpInput from '../../../components/OtpInput/OtpInput';
import AppErrorPopup from '../../../components/AppErrorPopup';
import { useAxios } from '../../../common/api/hooks/useAxios';
import { URL } from '../../../common/api/urls';
import { TEXT_MSGS, OTP_RESEND_TIMER_VAL } from '../../../common/utility/constant';
import {
  getContactValidationError,
  toContactDigits,
  toContactTelInput,
} from '../../../common/utility/validators';

const fieldSx = {
  '& .MuiInputBase-root': {
    color: '#fff',
    bgcolor: 'rgba(255,255,255,0.06)',
    borderRadius: '6px',
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.65)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#8eb6ff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.26)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.5)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#5b9dff',
  },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)', mx: 0 },
};

const primaryBtnSx = {
  py: 1.1,
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 600,
  bgcolor: '#3d7eff',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#2f6ae6', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: 'rgba(61,126,255,0.35)', color: 'rgba(255,255,255,0.45)' },
};

export default function MobileOtpVerificationDialog({
  open,
  contact: contactProp,
  displayContact: displayContactProp,
  registrationPayload,
  onClose,
  onRegisterSuccess,
  email,
  onVerifySuccess,
  onSkip,
  allowEditContact = false,
  dismissible = true,
  initialOtpSent = false,
}) {
  const isAuthenticatedMode = Boolean(email && onVerifySuccess);
  const [contactInput, setContactInput] = useState('');
  const [activeContact, setActiveContact] = useState(null);
  const [otp, setOtp] = useState('');
  const [resendCounter, setResendCounter] = useState(0);
  const [msgState, setMsgState] = useState({ open: false, msg: '', severity: 'error' });
  const [actionState, setActionState] = useState('idle');
  const [contactError, setContactError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendAttempted, setSendAttempted] = useState(false);
  const isManualResend = useRef(false);
  const registrationAutoSendDone = useRef(false);
  const openInitialized = useRef(false);

  const {
    cancel: sendCancel,
    responseData: sendResponse,
    error: sendError,
    loaded: sendLoaded,
    reset: sendReset,
    executeAPI: sendOtp,
  } = useAxios();

  const {
    cancel: verifyCancel,
    responseData: verifyResponse,
    error: verifyError,
    loaded: verifyLoaded,
    reset: verifyReset,
    executeAPI: verifyOtpApi,
  } = useAxios();

  const {
    cancel: registerCancel,
    responseData: registerResponse,
    error: registerError,
    loaded: registerLoaded,
    reset: registerReset,
    executeAPI: registerUser,
  } = useAxios();

  const showError = useCallback((message) => {
    setMsgState({
      open: true,
      msg: message ?? TEXT_MSGS.NETWORK_ERROR_MSG,
      severity: 'error',
    });
  }, []);

  const showSuccess = useCallback((message) => {
    setMsgState({
      open: true,
      msg: message,
      severity: 'success',
    });
  }, []);

  const resetDialogState = useCallback(() => {
    setOtp('');
    setResendCounter(0);
    setActionState('idle');
    setContactError('');
    setOtpSent(false);
    setSendAttempted(false);
    isManualResend.current = false;
    registrationAutoSendDone.current = false;
    sendReset();
    verifyReset();
    registerReset();
  }, [sendReset, verifyReset, registerReset]);

  useEffect(() => {
    if (!open) {
      resetDialogState();
      setActiveContact(null);
      setContactInput('');
      openInitialized.current = false;
      return;
    }
    if (openInitialized.current) return;
    openInitialized.current = true;

    const digits = toContactDigits(contactProp);
    const initialTel = toContactTelInput(contactProp);
    const initial = digits.length >= 4 && digits.length <= 15 ? digits : null;
    if (allowEditContact) {
      setActiveContact((prev) => prev || initial);
      setContactInput((prev) => prev || initialTel || '');
    } else {
      setActiveContact(initial || digits || null);
    }
    if (initialOtpSent && !isAuthenticatedMode) {
      setOtpSent(true);
      setSendAttempted(true);
      setResendCounter(OTP_RESEND_TIMER_VAL);
      registrationAutoSendDone.current = true;
    }
  }, [open, contactProp, allowEditContact, resetDialogState, initialOtpSent, isAuthenticatedMode]);

  const requestSendOtp = useCallback((contactToSend) => {
    if (!contactToSend) return;
    setActionState('sending');
    sendOtp(URL.SEND_MOBILE_VERIFY_OTP, 'POST', JSON.stringify({ contact: contactToSend }));
  }, [sendOtp]);

  useEffect(() => {
    if (!open || isAuthenticatedMode || initialOtpSent || registrationAutoSendDone.current) return;
    const digits = toContactDigits(contactProp);
    if (digits.length < 4 || digits.length > 15) return;
    registrationAutoSendDone.current = true;
    requestSendOtp(digits);
  }, [open, isAuthenticatedMode, initialOtpSent, contactProp, requestSendOtp]);

  useEffect(() => {
    if (resendCounter <= 0) return undefined;
    const timer = setTimeout(() => {
      setResendCounter((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCounter]);

  useEffect(() => {
    if (!sendLoaded) return;
    setSendAttempted(true);
    if (sendResponse != null) {
      setOtpSent(true);
      setResendCounter(OTP_RESEND_TIMER_VAL);
      setActionState('idle');
      if (isManualResend.current && sendResponse.message) {
        showSuccess(sendResponse.message);
        isManualResend.current = false;
      }
    } else if (sendError != null) {
      setActionState('idle');
      const message = sendError?.response?.data?.message ?? TEXT_MSGS.NETWORK_ERROR_MSG;
      showError(message);
    }
    sendReset();
  }, [sendLoaded, sendResponse, sendError, sendReset, showError, showSuccess]);

  useEffect(() => {
    if (!verifyLoaded) return;
    if (verifyResponse != null) {
      setActionState('idle');
      onVerifySuccess();
    } else if (verifyError != null) {
      setActionState('idle');
      showError(verifyError?.response?.data?.message);
    }
    verifyReset();
  }, [verifyLoaded, verifyResponse, verifyError, verifyReset, showError, onVerifySuccess]);

  useEffect(() => {
    if (!registerLoaded) return;
    if (registerResponse != null) {
      setActionState('idle');
      onRegisterSuccess(registrationPayload.email_id);
    } else if (registerError != null) {
      setActionState('idle');
      showError(registerError?.response?.data?.message);
    }
    registerReset();
  }, [registerLoaded, registerResponse, registerError, registerReset, onRegisterSuccess, registrationPayload?.email_id, showError]);

  const resolveContact = () => {
    if (allowEditContact) {
      const validationError = getContactValidationError(contactInput);
      if (validationError) {
        setContactError(validationError);
        return null;
      }
      setContactError('');
      const digits = toContactDigits(contactInput);
      setActiveContact(digits);
      return digits;
    }
    const digits = toContactDigits(activeContact || '');
    if (!digits) {
      setContactError('Mobile number is required');
      return null;
    }
    setContactError('');
    return digits;
  };

  const handleSendOrResend = () => {
    if (resendCounter > 0 || actionState !== 'idle') return;
    const contactToSend = resolveContact();
    if (!contactToSend) return;
    setOtp('');
    isManualResend.current = otpSent;
    requestSendOtp(contactToSend);
  };

  const handleRegister = () => {
    const contact = resolveContact();
    if (!contact) return;
    if (otp.length !== 6) {
      showError('Please enter the 6-digit OTP.');
      return;
    }
    setActionState('registering');
    registerUser(URL.REGISTER, 'POST', JSON.stringify({ ...registrationPayload, contact, otp }));
  };

  const handlePrimaryAction = () => {
    if (actionState !== 'idle') return;
    if (isAuthenticatedMode) {
      if (!otpSent) {
        handleSendOrResend();
        return;
      }
      const contact = resolveContact();
      if (!contact) return;
      if (otp.length !== 6) {
        showError('Please enter the 6-digit OTP.');
        return;
      }
      setActionState('verifying');
      verifyOtpApi(URL.VERIFY_OTP, 'POST', JSON.stringify({ contact, otp, email }));
      return;
    }
    if (!otpSent) return;
    handleRegister();
  };

  const handleSkip = () => {
    if (actionState !== 'idle' || !isAuthenticatedMode) return;
    onSkip?.();
  };

  const handleCloseRequest = useCallback(() => {
    sendCancel();
    verifyCancel();
    registerCancel();
    resetDialogState();
    openInitialized.current = false;
    onClose?.();
  }, [sendCancel, verifyCancel, registerCancel, resetDialogState, onClose]);

  const handleDialogClose = () => {
    if (!dismissible) return;
    if (isAuthenticatedMode && actionState !== 'idle') return;
    handleCloseRequest();
  };

  const isBusy = actionState !== 'idle';
  const displayContact = displayContactProp
    || (activeContact ? String(activeContact) : '')
    || toContactDigits(contactInput);

  let primaryLabel = 'Verify Mobile Number and Register';
  if (isAuthenticatedMode) {
    if (actionState === 'verifying') primaryLabel = 'Verifying...';
    else if (otpSent) primaryLabel = 'Verify';
    else primaryLabel = 'Verify Mobile Number Now';
  } else if (actionState === 'sending') {
    primaryLabel = 'Sending OTP...';
  } else if (actionState === 'registering') {
    primaryLabel = 'Creating account...';
  } else if (otpSent) {
    primaryLabel = 'Register';
  } else {
    primaryLabel = 'Sending OTP...';
  }

  const resendLabel =
    actionState === 'sending'
      ? 'Sending OTP...'
      : resendCounter > 0
        ? `Please wait ${resendCounter}s before requesting a new OTP`
        : 'Resend OTP';

  const contactInputValid = !allowEditContact || !getContactValidationError(contactInput);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        disableEscapeKeyDown={!dismissible}
        maxWidth={false}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(13, 33, 71, 0.55)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          },
        }}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 424,
            m: 2,
            bgcolor: '#0d2147',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.16)',
            borderRadius: '8px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.3 }}>
            Quick contact update needed
          </Typography>
          <Typography sx={{ mt: 0.75, mb:0.5, fontStyle: 'italic', fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
            {isAuthenticatedMode
              ? (otpSent
                ? `Enter the 6-digit OTP sent to WhatsApp on ${displayContact}.`
                : 'Exchange norms require a verified mobile number on every account. Verify once via WhatsApp OTP and continue as usual.')
              : (otpSent
                ? `Enter the 6-digit OTP sent to WhatsApp on ${displayContact}.`
                : `Sending OTP to WhatsApp on ${displayContact}...`)}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          {allowEditContact && (
            <MuiTelInput
              fullWidth
              size="small"
              label="Mobile number"
              value={contactInput || ''}
              onChange={(value) => {
                setContactInput(value);
                setContactError('');
              }}
              error={Boolean(contactError)}
              helperText={contactError || ' '}
              disabled={isBusy || otpSent}
              defaultCountry="IN"
              forceCallingCode
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 280,
                    bgcolor: '#0d2147',
                    color: '#fff',
                  },
                },
              }}
              sx={{ ...fieldSx, mb: otpSent ? 1.5 : 2 }}
            />
          )}

          {otpSent && <OtpInput value={otp} onChange={setOtp} disabled={isBusy} />}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: otpSent ? 0.5 : 0 }}>
            {(isAuthenticatedMode || otpSent || sendAttempted) && (
            <Button
              variant="contained"
              onClick={handlePrimaryAction}
              disabled={
                isBusy
                || (isAuthenticatedMode && !otpSent && allowEditContact && !contactInputValid)
                || (!isAuthenticatedMode && !otpSent)
              }
              fullWidth
              sx={primaryBtnSx}
            >
              {primaryLabel}
            </Button>
            )}

            {otpSent && (
              <Button
                variant="text"
                onClick={handleSendOrResend}
                disabled={resendCounter > 0 || isBusy}
                fullWidth
                sx={{
                  py: 0.5,
                  textTransform: 'none',
                  color: resendCounter > 0 ? 'rgba(255,255,255,0.45)' : '#8eb6ff',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                {resendLabel}
              </Button>
            )}

            {!isAuthenticatedMode && sendAttempted && !otpSent && (
              <Button
                variant="text"
                onClick={handleSendOrResend}
                disabled={isBusy}
                fullWidth
                sx={{
                  py: 0.5,
                  textTransform: 'none',
                  color: '#8eb6ff',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                Retry sending OTP
              </Button>
            )}

            {isAuthenticatedMode && onSkip && (
              <Button
                variant="text"
                onClick={handleSkip}
                disabled={isBusy}
                fullWidth
                sx={{
                  py: 0.5,
                  textTransform: 'none',
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.85rem',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                Verify later
              </Button>
            )}

            {dismissible && (
              <Button
                variant="outlined"
                onClick={handleCloseRequest}
                disabled={isAuthenticatedMode && isBusy}
                fullWidth
                sx={{
                  py: 1,
                  borderRadius: '6px',
                  textTransform: 'none',
                  color: 'rgba(255,255,255,0.85)',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.55)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <AppErrorPopup
        open={msgState.open}
        message={msgState.msg}
        severity={msgState.severity}
        onClose={() => setMsgState((prev) => ({ ...prev, open: false }))}
        showCloseX
        autoCloseTimer={msgState.severity === 'success' ? 3000 : 0}
      />
    </>
  );
}
